import type { CSSProperties, ReactNode } from 'react';
import './ProgressCircle.css';

export type ProgressCircleStatus = 'not-started' | 'in-progress' | 'finish';

/**
 * Circular progress indicator.
 *
 * At the default 24px it is the compact step indicator (not-started, in-progress,
 * finish). Any larger `size`, or passing `children`, switches to **donut mode**:
 * a thicker ring (default stroke `size / 15`, i.e. 8px at 120px) on the sunken
 * surface track, with `children` centered inside — e.g. "1 of 2" credit usage.
 */
export type ProgressCircleProps = {
  status?: ProgressCircleStatus;
  /** 0–100 arc length when `status="in-progress"`. Values outside the range are clamped. */
  value?: number;
  /** Outer diameter in px. Default 24 (compact). */
  size?: number;
  /** Ring thickness in px. Default 2 at 24px, otherwise `round(size / 15)` (8 at 120px). */
  strokeWidth?: number;
  /** Any CSS color for the track ring. Defaults to a Spark border/surface token. */
  trackColor?: string;
  /** Any CSS color for the progress arc. Defaults to `--semantic-border-active`. */
  fillColor?: string;
  /** Content centered inside the ring (donut mode). */
  children?: ReactNode;
  className?: string;
};

export const PROGRESS_CIRCLE_COMPACT_SIZE = 24;
const COMPACT_STROKE_WIDTH = 2;
/** Compact ring sits 2px inside the 24px box (r=9 with a 2px stroke). */
const COMPACT_RADIUS_INSET = 2;

/** Stroked check aligned to the r=9 compact ring — same 2px weight as the circle stroke. */
const FINISH_CHECK_PATH = 'M9 12.25 L11.05 14.05 L15.1 10.4';

export function defaultProgressCircleStrokeWidth(size: number): number {
  return size <= PROGRESS_CIRCLE_COMPACT_SIZE ? COMPACT_STROKE_WIDTH : Math.max(1, Math.round(size / 15));
}

export function ProgressCircle({
  status = 'not-started',
  value = 65,
  size = PROGRESS_CIRCLE_COMPACT_SIZE,
  strokeWidth = defaultProgressCircleStrokeWidth(size),
  trackColor,
  fillColor,
  children,
  className = '',
}: ProgressCircleProps) {
  const isDonut = size > PROGRESS_CIRCLE_COMPACT_SIZE || children != null;
  const clamped = Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));
  const center = size / 2;
  const radius = Math.max(0, (size - strokeWidth) / 2 - (isDonut ? 0 : COMPACT_RADIUS_INSET));
  const circumference = 2 * Math.PI * radius;
  const dash = (circumference * clamped) / 100;
  const checkScale = size / PROGRESS_CIRCLE_COMPACT_SIZE;

  const rootClasses = ['spark-progress-circle', isDonut ? 'spark-progress-circle-donut' : '', className]
    .filter(Boolean)
    .join(' ');

  const rootStyle = {
    width: size,
    height: size,
    ...(trackColor ? { '--spark-progress-circle-track-color': trackColor } : {}),
    ...(fillColor ? { '--spark-progress-circle-fill-color': fillColor } : {}),
  } as CSSProperties;

  const content = children != null ? <span className="spark-progress-circle-content">{children}</span> : null;

  if (status === 'finish') {
    return (
      <span className={rootClasses} style={rootStyle} role="img" aria-label="Complete">
        <svg className="spark-progress-circle-svg" viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
          <circle
            className="spark-progress-circle-finish-ring"
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={strokeWidth}
          />
          <path
            className="spark-progress-circle-finish-check"
            d={FINISH_CHECK_PATH}
            transform={checkScale === 1 ? undefined : `scale(${checkScale})`}
            strokeWidth={strokeWidth / checkScale}
          />
        </svg>
        {content}
      </span>
    );
  }

  return (
    <span
      className={rootClasses}
      style={rootStyle}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={status === 'in-progress' ? clamped : 0}
      aria-label={status === 'not-started' ? 'Not started' : 'In progress'}
    >
      <svg className="spark-progress-circle-svg" viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle className="spark-progress-circle-track" cx={center} cy={center} r={radius} strokeWidth={strokeWidth} />
        {status === 'in-progress' ? (
          <circle
            className="spark-progress-circle-arc"
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={strokeWidth}
            strokeDasharray={`${dash} ${circumference}`}
          />
        ) : null}
      </svg>
      {content}
    </span>
  );
}
